import type { RecentActivityItem } from "../../recent-activity";

export interface FormattedActivity {
    avatar: string;
    color: string;
    iconType: string;
    text: string;
    time: string;
}

// Function to get time ago from ISO string
export const getTimeAgo = (isoString: string): string => {
    const now = new Date();
    const activityTime = new Date(isoString);
    const diffInMinutes = Math.floor((now.getTime() - activityTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
};

// Function to extract user name from activity title
const extractUserName = (title: string): string => {
    // For titles like "User just registered an account", we'll use a generic name
    if (title.toLowerCase().startsWith('user just')) {
        return 'Anonymous User';
    }

    // Common patterns in activity titles
    const patterns = [
        /User\s+(\w+)\s+(\w+)/, // "User John Doe registered"
        /(\w+)\s+(\w+)\s+just/, // "John Doe just registered"
        /(\w+)\s+(\w+)\s+joined/, // "John Doe joined"
        /(\w+)\s+(\w+)\s+completed/, // "John Doe completed"
        /(\w+)\s+(\w+)\s+earned/, // "John Doe earned"
        /(\w+)\s+(\w+)\s+exchanged/, // "John Doe exchanged"
        /(\w+)\s+(\w+)\s+submitted/, // "John Doe submitted"
    ];

    for (const pattern of patterns) {
        const match = title.match(pattern);
        if (match) {
            return `${match[1]} ${match[2]}`;
        }
    }

    // Fallback: extract first two words that look like names
    const words = title.split(' ').filter(word => /^[A-Z][a-z]+$/.test(word));
    if (words.length >= 2) {
        return `${words[0]} ${words[1]}`;
    }

    return 'Anonymous User';
};

// Function to get activity icon and color based on title
const getActivityIconAndColor = (title: string): { iconType: string; color: string } => {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('registered') || lowerTitle.includes('account')) {
        return {
            iconType: "user-plus",
            color: "bg-blue-500"
        };
    }

    if (lowerTitle.includes('redeemed') || lowerTitle.includes('reward') || lowerTitle.includes('coupon')) {
        return {
            iconType: "gift",
            color: "bg-yellow-500"
        };
    }

    if (lowerTitle.includes('survey') || lowerTitle.includes('completed')) {
        return {
            iconType: "clipboard-list",
            color: "bg-purple-500"
        };
    }

    if (lowerTitle.includes('joined') || lowerTitle.includes('event')) {
        return {
            iconType: "calendar-check",
            color: "bg-green-500"
        };
    }

    if (lowerTitle.includes('badge') || lowerTitle.includes('earned')) {
        return {
            iconType: "award",
            color: "bg-pink-500"
        };
    }

    // Default
    return {
        iconType: "activity",
        color: "bg-gray-400"
    };
};

// Function to format activity title for display
const formatActivityText = (title: string, userName: string): string => {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('registered')) {
        return `New user <b>${userName}</b> registered.`;
    }

    if (lowerTitle.includes('redeemed') || lowerTitle.includes('reward')) {
        return `<b>${userName}</b> exchanged a coupon.`;
    }

    if (lowerTitle.includes('survey')) {
        return `<b>${userName}</b> submitted survey.`;
    }

    if (lowerTitle.includes('joined') && lowerTitle.includes('event')) {
        // Extract event name if possible
        const eventMatch = title.match(/joined the '([^']+)'/);
        const eventName = eventMatch ? eventMatch[1] : 'an event';
        return `<b>${userName}</b> joined event '${eventName}'.`;
    }

    if (lowerTitle.includes('badge') || lowerTitle.includes('earned')) {
        return `<b>${userName}</b> earned a new badge.`;
    }

    // For "User just..." titles, format them properly
    if (title.toLowerCase().startsWith('user just')) {
        const action = title.toLowerCase().replace('user just ', '');
        return `A user <b>${action}</b>.`;
    }

    // Fallback: use original title with user name highlighted
    return `<b>${userName}</b> ${title.replace(/^User\s+/, '').toLowerCase()}.`;
};

// Function to format points numbers (e.g., 10.000000 -> 10)
const formatPoints = (text: string): string => {
    // Find patterns like "10.000000 points" and replace with "10 points"
    return text.replace(/(\d+)\.0+(\s+points?)/g, '$1$2');
};

export const formatActivity = (activity: RecentActivityItem): FormattedActivity => {
    const { iconType, color } = getActivityIconAndColor(activity.title);
    const timeAgo = getTimeAgo(activity.created_at);

    // Format the title to clean up points display
    const formattedText = formatPoints(activity.title);

    return {
        avatar: "U", // Use "U" for User
        color,
        iconType,
        text: formattedText, // Use formatted title
        time: timeAgo
    };
}; 
